import { useCallback, useEffect, useState } from "react";
import { Check, X, Inbox } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import { formatDate } from "@/lib/utils";
import { listPendingReviews, reviewTimesheet } from "@/api/time";
import type { PendingReviewRow } from "@/types/time";

export function PendingReviewsPanel() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<PendingReviewRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listPendingReviews();
      setRows(data);
    } catch {
      addToast("error", "Failed to load pending reviews");
    } finally {
      setIsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, action: "APPROVED" | "REJECTED") => {
    try {
      await reviewTimesheet(id, action);
      addToast("success", `Timesheet ${action.toLowerCase()}`);
      await load();
    } catch {
      addToast("error", "Failed to update");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <p className="text-sm text-text-muted text-center py-6">Loading…</p>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <div className="py-8 text-center">
          <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40 text-text-muted" />
          <p className="text-sm text-text-muted">No pending timesheets.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <Card key={r.timesheet_id} className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-text-primary">{r.employee_name}</p>
              {r.overtime_hours > 0 && (
                <Badge variant="warning">{r.overtime_hours}h OT</Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-text-muted">
              <span>
                {formatDate(r.week_start, "MMM d")} —{" "}
                {formatDate(r.week_end, "MMM d, yyyy")}
              </span>
              <span>{r.total_hours}h total</span>
              <span>{r.regular_hours}h regular</span>
              {r.submitted_at && (
                <span>submitted {formatDate(r.submitted_at, "MMM d HH:mm")}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={() => act(r.timesheet_id, "APPROVED")}
            >
              <Check className="h-4 w-4" />
              Approve
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => act(r.timesheet_id, "REJECTED")}
            >
              <X className="h-4 w-4" />
              Reject
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
